import React, { useEffect, useState } from "react";
import AdminLayout from "@/components/layout/AdminLayout";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { LayoutGrid, Eye, EyeOff } from "lucide-react";

const FILTER_LABELS = { awards: "Awards", commitments: "Commitments", rankings: "Rankings", featured: "Featured", metrics: "Metrics" };

export default function AdminMediaWall() {
  const [items, setItems] = useState([]);
  const [filters, setFilters] = useState([]);
  const [active, setActive] = useState("all");

  const load = () => api.get("/admin/media-wall").then(({ data }) => { setItems(data.items || []); setFilters(data.filters || []); });
  useEffect(() => { load(); }, []);

  const toggle = async (it) => {
    try {
      await api.post(`/admin/media-wall/${it.id}/visibility`, { hidden: !it.wall_hidden });
      setItems((arr) => arr.map((x) => x.id === it.id ? { ...x, wall_hidden: !x.wall_hidden } : x));
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || e.message); }
  };

  const shown = active === "all" ? items : items.filter((i) => i.category === active);
  const visibleCount = items.filter((i) => !i.wall_hidden).length;

  return (
    <AdminLayout>
      <div className="p-4 sm:p-8 max-w-6xl">
        <div className="flex items-center gap-2 label-overline text-[#E63946]"><LayoutGrid className="w-4 h-4" /> Media Wall</div>
        <h1 className="font-display text-3xl sm:text-4xl uppercase tracking-tighter mt-2">Media Wall Manager</h1>
        <p className="text-sm text-slate-500 mt-2">Approved & posted Media Engine graphics. Hide any card you don't want on the public wall. <span className="font-semibold text-[#0A192F]">{visibleCount} of {items.length} visible.</span></p>

        <div className="flex flex-wrap gap-1 mt-6 mb-6">
          {["all", ...filters].map((f) => (
            <button key={f} data-testid={`mw-filter-${f}`} onClick={() => setActive(f)}
              className={`px-3 py-1.5 text-xs font-semibold uppercase tracking-wider rounded-sm ${active === f ? "bg-[#0A192F] text-white" : "bg-white border border-slate-200 text-slate-500 hover:border-[#0A192F]"}`}>
              {f === "all" ? "All" : (FILTER_LABELS[f] || f)}
            </button>
          ))}
        </div>

        {shown.length === 0 ? (
          <div className="bg-white border border-dashed border-slate-300 rounded-sm p-10 text-center text-sm text-slate-500">No approved graphics yet. Approve drafts in the Media Engine to populate the wall.</div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3" data-testid="mw-grid">
            {shown.map((it) => (
              <div key={it.id} data-testid={`mw-card-${it.id}`} className={`bg-white border rounded-sm overflow-hidden ${it.wall_hidden ? "border-slate-200 opacity-60" : "border-slate-200"}`}>
                <div className="aspect-square bg-[#0A192F] relative">
                  <img src={it.card_image_url} alt={it.player_name || ""} className="w-full h-full object-cover" loading="lazy" />
                  <span className="absolute top-1.5 left-1.5 text-[9px] font-semibold uppercase tracking-wider bg-[#E63946] text-white px-1.5 py-0.5 rounded-sm">{FILTER_LABELS[it.category] || it.category}</span>
                </div>
                <div className="p-2">
                  <div className="text-xs font-semibold truncate">{it.player_name || "—"}</div>
                  <button data-testid={`mw-toggle-${it.id}`} onClick={() => toggle(it)}
                    className={`mt-2 w-full inline-flex items-center justify-center gap-1 px-2 py-1.5 text-[10px] font-semibold uppercase tracking-wider rounded-sm ${it.wall_hidden ? "bg-slate-100 text-slate-400" : "bg-emerald-100 text-emerald-700"}`}>
                    {it.wall_hidden ? <><EyeOff className="w-3.5 h-3.5" /> Hidden</> : <><Eye className="w-3.5 h-3.5" /> Shown</>}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
